import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import { Button } from '../../../design-system/components/core';
import { Card, MagicLoader, TextField } from '../../../design-system';
import Breadcrumbs from '../../../components/core/Breadcrumbs';
import useExpenseStore from '../../../stores/expenseStore';
import useUIStore from '../../../stores/uiStore';

const ReimburseExpense = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const {
    currentExpense,
    isLoading,
    fetchExpenseById,
    markAsReimbursed
  } = useExpenseStore();
  
  const { addNotification } = useUIStore();
  
  const [formData, setFormData] = useState({
    reference: '',
    amount: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchExpenseById(id);
  }, [id, fetchExpenseById]);

  useEffect(() => {
    if (currentExpense) {
      setFormData(prev => ({
        ...prev,
        amount: currentExpense.totalAmount
      }));
    }
  }, [currentExpense]);

  const breadcrumbs = [
    { label: 'Finances', path: '/finances' },
    { label: 'Expenses', path: '/finances/expenses' },
    { label: currentExpense?.expenseNumber || 'Loading...', path: `/finances/expenses/${id}` },
    { label: 'Reimburse' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reference) newErrors.reference = 'Reference is required';
    if (!formData.amount) newErrors.amount = 'Amount is required';
    if (formData.amount && formData.amount <= 0) newErrors.amount = 'Amount must be greater than 0';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      addNotification({
        message: 'Please fix the errors in the form',
        type: 'error'
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      await markAsReimbursed(id, {
        reference: formData.reference,
        amount: parseFloat(formData.amount)
      });
      
      addNotification({
        message: 'Expense marked as reimbursed',
        type: 'success'
      });
      navigate(`/finances/expenses/${id}`);
    } catch (error) {
      addNotification({
        message: 'Failed to reimburse expense',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/finances/expenses/${id}`);
  };

  if (isLoading || !currentExpense) {
    return <MagicLoader />;
  }

  return (
    <Box>
      <Breadcrumbs items={breadcrumbs} />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/finances/expenses/${id}`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4">
            Reimburse Expense
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <Typography variant="h6" gutterBottom>
              Expense Details
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Expense Number
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.expenseNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(currentExpense.date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    ${currentExpense.amount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Total Amount (with tax)
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    ${currentExpense.totalAmount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Owner
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.owner.firstName} {currentExpense.owner.lastName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Reimbursement Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Reference Number"
                  value={formData.reference}
                  onChange={(e) => handleChange('reference', e.target.value)}
                  error={!!errors.reference}
                  helperText={errors.reference}
                  placeholder="e.g., Check #1234, Transfer #5678"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Reimbursement Amount"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  type="number"
                  error={!!errors.amount}
                  helperText={errors.amount}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  required
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                loading={isSaving}
                fullWidth
              >
                Mark as Reimbursed
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                fullWidth
              >
                Cancel
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReimburseExpense;